/**
 * Read-only field-level diff view for a config version.
 *
 * Renders a `VersionDiff` (`from` = current live value, `to` = value after
 * reverting) as a compact before/after table via {@link PreviewTable}. When
 * there are no changes an informational `Alert` is shown instead.
 */

import { Alert } from '@components/base/feedback';
import { Text } from '@components/base/display';
import { VStack } from '@components/base/layout';

import type { VersionDiff } from '../../api/configVersioningService';
import { PreviewTable } from './PreviewTable';
import { formatDiffValue } from './configVersioningFormat';

export interface VersionDiffViewProps {
  diff: VersionDiff;
}

export function VersionDiffView({ diff }: Readonly<VersionDiffViewProps>) {
  if (diff.changes.length === 0) {
    return (
      <Alert variant="info" title="Aucune différence">
        La configuration est déjà identique à cette version — la restauration
        n’aurait aucun effet.
      </Alert>
    );
  }

  const rows = diff.changes.map((change) => ({
    id: change.field,
    field: change.field,
    from: formatDiffValue(change.from),
    to: formatDiffValue(change.to),
  }));

  return (
    <VStack gap={2}>
      <Text size="sm" color="muted">
        {diff.changes.length} champ{diff.changes.length > 1 ? 's' : ''} modifié
        {diff.changes.length > 1 ? 's' : ''} — version cible n°
        {diff.target_version} vs version actuelle n°{diff.current_version}
      </Text>
      <PreviewTable
        id="config-version-diff"
        columns={[
          { key: 'field', label: 'Champ' },
          { key: 'from', label: 'Valeur actuelle' },
          { key: 'to', label: 'Après restauration' },
        ]}
        rows={rows}
        emptyMessage="Aucune différence"
      />
    </VStack>
  );
}
